import { css } from '@emotion/react';
import { useMemo } from 'react';

import IconChevronLeft from '~icons/ci/chevron-left';
import IconChevronRight from '~icons/ci/chevron-right';

import { theme } from '@adflow/frontend/ui-theme';

import type { CalculatePageOptionsOptions } from './calculatePageOptions';
import { calculatePageOptions } from './calculatePageOptions';

const styles = {
	buttonsWrap: css({ display: 'flex', alignItems: 'center' }),
	button: css(
		{
			backgroundColor: theme.colors.background.paper,
			borderColor: theme.colors.input.border,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			height: '2rem',
			borderWidth: '1px',

			color: theme.colors.text.primary,
		},
		{
			alignItems: 'center',
			cursor: 'default',

			borderRight: 'none',
			minWidth: 32,
		}
	),

	rightArrowBtn: css({
		borderRight: 'solid 1px',
		borderColor: theme.colors.input.border,
	}),

	buttonActive: css({
		'&:hover': { backgroundColor: theme.colors.background.tableHeader },
		cursor: 'pointer',
	}),
	buttonSelected: css({
		backgroundColor: theme.colors.input.border,
		cursor: 'default',
	}),
};

export interface ListPageSelectorProps {
	totalItems?: number;
	rowsPerPage: number;
	page: number;
	onChangePage: (newPage: number) => void;
	delta?: number;
	alwaysShowFirstPage?: boolean;
	alwaysShowLastPage?: boolean;
}

export function ListPageSelector({
	totalItems = 0,
	rowsPerPage,
	page,
	onChangePage,
	delta = 2,
	alwaysShowFirstPage = true,
	alwaysShowLastPage = true,
}: ListPageSelectorProps) {
	const lastPage = useMemo(() => {
		if (!totalItems || !rowsPerPage) {
			return 1;
		}
		return Math.max(1, Math.ceil(totalItems / rowsPerPage));
	}, [rowsPerPage, totalItems]);

	return (
		<ListPageSelectorImpl
			firstPage={1}
			currentPage={page + 1}
			onChangePage={(newPage) => onChangePage(newPage - 1)}
			lastPage={lastPage}
			delta={delta}
			alwaysShowFirstPage={alwaysShowFirstPage}
			alwaysShowLastPage={alwaysShowLastPage}
		/>
	);
}

export interface ListPageSelectorImplProps extends CalculatePageOptionsOptions {
	onChangePage: (newPage: number) => void;
}

export function ListPageSelectorImpl({
	onChangePage,
	firstPage,
	currentPage,
	lastPage,
	delta,
	alwaysShowFirstPage,
	alwaysShowLastPage,
}: ListPageSelectorImplProps) {
	const pageButtonOptions = useMemo(
		() =>
			calculatePageOptions({
				firstPage,
				currentPage,
				lastPage,
				delta,
				alwaysShowFirstPage,
				alwaysShowLastPage,
			}),
		[
			firstPage,
			currentPage,
			lastPage,
			delta,
			alwaysShowFirstPage,
			alwaysShowLastPage,
		]
	);

	return (
		<div css={styles.buttonsWrap}>
			<button
				type="button"
				css={[
					styles.button,
					firstPage <= currentPage - 1 && styles.buttonActive,
				]}
				onClick={() => onChangePage(currentPage - 1)}
				disabled={firstPage > currentPage - 1}
				data-testid="pagination-left"
			>
				<IconChevronLeft width={20} height={20} />
			</button>
			{pageButtonOptions.map((option, i) => {
				const isSelectedPage = currentPage === +option;
				return (
					<button
						key={`${option}${i}`}
						type="button"
						css={[
							styles.button,
							option !== '...' && styles.buttonActive,
							isSelectedPage && styles.buttonSelected,
						]}
						onClick={() => {
							if (typeof option === 'number') {
								onChangePage(option);
							}
						}}
						disabled={option === '...'}
						data-pagination-selected={isSelectedPage}
						data-testid="pagination-option"
					>
						{option.toLocaleString()}
					</button>
				);
			})}
			<button
				type="button"
				css={[
					styles.button,
					styles.rightArrowBtn,
					currentPage + 1 <= lastPage && styles.buttonActive,
				]}
				onClick={() => onChangePage(currentPage + 1)}
				disabled={currentPage + 1 > lastPage}
				data-testid="pagination-right"
			>
				<IconChevronRight width={20} height={20} />
			</button>
		</div>
	);
}
